<script setup lang="ts">
import { DAYS } from '~/config/stores'
import { SingleStoreProps } from './StoreComponentProps'
import { SpecialStoreHour } from '~/types/Store'

const props = defineProps<
  SingleStoreProps & {
    isAccordion?: boolean
    isAccordionOpen?: boolean
    showAllSpecialHours?: boolean
    gaOptions?: Omit<DataLayerObject, 'eventName' | 'event'>
  }
>()
const emit = defineEmits<{
  (e: 'toggle', isOpen: boolean): void
}>()
const i18n = useI18n()
const locale = i18n.locale.value
const currentDate = new Date()
const currentDay = currentDate.toLocaleString(localeConfig[locale].code, { weekday: 'long' })
const storeUrl = getStoreUrl(props.store, locale === 'fr' ? '/fr/stores' : '/stores')

// Helper to parse ISO date string as local date (not UTC)
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const specialHours = computed(() => {
  if (props.store.c_specialStoreHoursJson) {
    const parsedSpecialHoursData: SpecialStoreHour[] = JSON.parse(props.store.c_specialStoreHoursJson)
    const transformedData = parsedSpecialHoursData
      .map(({ special_date, ...rest }) => ({
        ...rest,
        special_date: parseLocalDate(special_date),
      }))
      .filter(({ special_date }) => {
        return special_date > currentDate || isCurrentDate(special_date)
      })

    return !props.showAllSpecialHours
      ? transformedData.filter(({ special_date }) => {
          const sevenDaysFromNow = new Date(currentDate)
          sevenDaysFromNow.setDate(currentDate.getDate() + 7)
          return special_date <= sevenDaysFromNow
        })
      : transformedData
  }
  return []
})

const formatStoreHours = computed(() => {
  const formatHours: { [key in (typeof DAYS)[number]]: { start: string; end: string } } = {}

  DAYS.forEach((day) => {
    const start = props.store[`c_storeHours${day}Start`]
    const end = props.store[`c_storeHours${day}End`]

    formatHours[day] = {
      start: start ? start.replace(' ', '') : '',
      end: end ? end.replace(' ', '') : '',
    }
  })

  return formatHours
})

const formatSpecialHour = (day: string): string => {
  const formattedTime = day.replace(/^0|\s/g, '')
  return formattedTime
}

const formatSpecialDate = (date: Date): string => {
  const formattedDate = date.toLocaleDateString(localeConfig[locale].code, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  return formattedDate.replace(/,/, '')
}

const isCurrentDay = (day: string | undefined): boolean => {
  return day === currentDay
}

const isCurrentDate = (date: Date): boolean => {
  return date.toDateString() === currentDate.toDateString()
}

const localisedDayNames = computed(() => {
  const days = DAYS
  return days.map((day) => i18n.t(`days.${day.toLowerCase()}`))
})

const isStoreOpen = (store: SFCCStore) => {
  const storeDayStart = store[`c_storeHours${currentDay}Start`]
  const storeDayEnd = store[`c_storeHours${currentDay}End`]
  if (storeDayStart && storeDayEnd) {
    const currentTime = parseInt(
      new Date().toLocaleTimeString(localeConfig[locale].code, { hour: 'numeric', hour12: false }),
      10,
    )
    const storeDayStartHour = parseInt(convertTo24HourFormat(storeDayStart).split(':')[0] || '', 10)
    const storeDayEndHour = parseInt(convertTo24HourFormat(storeDayEnd).split(':')[0] || '', 10)

    return currentTime >= storeDayStartHour && currentTime <= storeDayEndHour
  }
  return false
}

const convertTo24HourFormat = (time: string) => {
  const [hours, minutes, period] = time.split(/:|\s/)
  let convertedHours = parseInt(hours || '', 10)

  if (period === 'PM' && convertedHours !== 12) {
    convertedHours += 12
  } else if (period === 'AM' && convertedHours === 12) {
    convertedHours = 0
  }

  return `${convertedHours}:${minutes}`
}
</script>

<template>
  <div class="store-hours" :class="{ 'store-hours--accordion': isAccordion }">
    <Accordion
      :open="isSlideIn || isAccordionOpen"
      class="store-hours__accordion"
      :ga-options
      :title="store.name"
      @toggle="emit('toggle', $event)"
    >
      <template #title>
        <div class="store-hours__hours-title-wrapper">
          <p class="store-hours__hours-title">{{ $t('store_page.opening_hours') }}</p>
          <p v-if="isStoreOpen(store)" class="store-hours__open-hours">
            {{ $t('store_page.store_open') }} {{ formatStoreTime(formatStoreHours[currentDay]?.end) }}
          </p>
        </div>
      </template>

      <dl class="store-hours__list">
        <template v-for="(day, index) in DAYS" :key="index">
          <div class="store-hours__item" :class="{ 'is-active': isCurrentDay(localisedDayNames[index]) }">
            <dt class="store-hours__title">
              {{ localisedDayNames[index] }}
            </dt>
            <dd class="store-hours__description">
              <span>{{ formatStoreHours[day]?.start }}</span>
              <span>{{ formatStoreHours[day]?.end }}</span>
            </dd>
          </div>
        </template>
      </dl>

      <dl v-if="specialHours.length" class="store-hours__list store-hours__list--secondary">
        <div class="store-hours__list-title">{{ $t('store_page.special_hours') }}</div>
        <template v-for="(day, index) in specialHours" :key="index">
          <div class="store-hours__item" :class="{ 'is-active': isCurrentDate(day.special_date) }">
            <dt class="store-hours__title">
              {{ formatSpecialDate(day.special_date) }}
            </dt>
            <dd v-if="day.isClosed" class="store-hours__description store-hours__description--is-closed">
              <span>{{ $t('store_page.closed') }}</span>
            </dd>
            <dd v-else class="store-hours__description">
              <span>{{ formatSpecialHour(day.open) }}</span> <span>{{ formatSpecialHour(day.close) }}</span>
            </dd>
          </div>
        </template>
        <Anchor v-if="!showAllSpecialHours" :href="storeUrl" class="store-hours__link">
          {{ $t('store_page.view_all_hours') }}
        </Anchor>
      </dl>
    </Accordion>
  </div>
</template>

<style lang="postcss">
.store-hours {
  &.store-hours--accordion {
    .accordion.store-hours__accordion summary {
      pointer-events: auto;
    }
    .accordion.store-hours__accordion summary::after {
      display: block;
      top: -0.5rem;
    }
    .store-hours__hours-title-wrapper {
      margin-right: var(--space-80);
    }
  }
  .accordion.store-hours__accordion summary {
    pointer-events: none;
    padding-top: var(--space-30);
    padding-bottom: var(--space-30);
  }
  .accordion.store-hours__accordion summary::after {
    display: none;
  }
  .store-hours__hours-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-right: 0;
  }

  .store-hours__hours-title {
    font-family: var(--font-base);
    font-size: var(--text-base);
    font-weight: 500;
    padding: 0;
    margin-bottom: 0;
  }
  .store-hours__open-hours {
    font-weight: 400;
    margin-bottom: 0;
  }
  .store-hours__list {
    list-style: none;
    margin-top: 0;
    margin-bottom: 0;

    .store-hours__list-title {
      color: var(--text-primary);
      font-family: var(--font-base);
      font-weight: bold;
      margin-bottom: var(--space-30);
    }
  }

  .store-hours__item {
    display: grid;
    grid-template-columns: minmax(min-content, 1fr) 1fr;
    margin-bottom: var(--space-20);
    padding-block: var(--space-20);

    .store-hours__list--secondary {
      grid-template-columns: 6fr 4fr;
      max-width: 19.5rem;
    }
  }
  .store-hours__link {
    padding-left: var(--space-50);
  }

  .is-active {
    background-color: var(--ui-neutral-20);
    font-family: var(--font-base);
    font-weight: bold;
  }

  .store-hours__title {
    padding: 0 var(--space-30) 0 var(--space-50);
    min-width: fit-content;
    text-wrap: nowrap;
  }

  .store-hours__description {
    text-transform: lowercase;
    align-items: center;
    display: flex;
    gap: var(--space-60);
    margin: 0;
    span {
      min-width: var(--space-100);
    }

    .store-hours__description--is-closed {
      text-transform: capitalize;
    }
  }
}
</style>
